import { DollarSignIcon } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import { FormNumberFieldDS } from 'components/reusable/form/FormFields'
import { ButtonGroup, Modal } from 'slab'
import { usePutQuotingPricesQuoteId as useUpdateQuotePricing } from 'utils/web_api'
import { type QuotingQuotePricing } from 'utils/web_api.schemas'

import { useQuotePricing } from './QuotePricingProvider'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  employeeCount: number
}

export default function EditPricingModal ({ open, onOpenChange, employeeCount }: Props) {
  const { quoteId, quotePricing, hasPricing, refetch } = useQuotePricing()

  const updateQuotePricingMutation = useUpdateQuotePricing()

  const { control, register, handleSubmit, formState: { errors }, reset } = useForm<QuotingQuotePricing>({
    defaultValues: quotePricing,
  })

  const watchedValues = useWatch({ control })

  useEffect(() => {
    if (quotePricing) {
      reset(quotePricing)
    }
  }, [quotePricing, reset])

  const onOpenChangeInternal = (open: boolean) => {
    if (!open) {
      reset(quotePricing)
    }
    onOpenChange(open)
  }

  const onSubmit = async (data: QuotingQuotePricing) => {
    return new Promise<boolean>((resolve) => {
      updateQuotePricingMutation.mutate({
        quoteId,
        data,
      }, {
        onSuccess: () => {
          resolve(true)
          refetch()
        },
        onError: () => {
          resolve(false)
        },
      })
    })
  }

  const onClickSave = async () => {
    return new Promise<boolean>((resolve) => {
      handleSubmit(async (data) => {
        const result = await onSubmit(data)
        resolve(result)
      }, () => {
        resolve(false)
      })()
    })
  }

  const formFields = useMemo(() => {
    if (!quotePricing || !hasPricing) {
      return []
    }

    const description = (fieldKey: keyof QuotingQuotePricing) => {
      const price = watchedValues?.[fieldKey] ?? 0

      return `$${(price * employeeCount).toFixed(2)}/mo for ${employeeCount} employee${employeeCount > 1 ? 's' : ''}`
    }

    const fields: {
      key: keyof QuotingQuotePricing
      label: string
      description?: string
    }[] = [
      {
        key: 'base_compliance_price',
        label: 'Monthly Compliance fee',
      }, {
        key: 'platform_price',
        label: 'Platform fee',
        description: description('platform_price'),
      }, {
        key: 'advising_price',
        label: 'Advisor fee',
        description: description('advising_price'),
      }, {
        key: 'core_compliance_price',
        label: 'Core Compliance fee',
        description: description('core_compliance_price'),
      }, {
        key: 'aca_compliance_price',
        label: 'ACA & COBRA compliance fee',
        description: description('aca_compliance_price'),
      },
    ]

    return fields.map((field) => {
      return (
        <div key={field.key}>
          <FormNumberFieldDS<QuotingQuotePricing>
            fieldId={field.key}
            fieldLabel={field.label}
            leadingIcon={DollarSignIcon}
            register={register}
            placeholder='/mo'
            description={field.description}
            step='0.01'
            validationRule={{
              min: {
                value: 0,
                message: 'Price must be greater than 0',
              },
            }}
            error={errors[field.key]}
          />
        </div>
      )
    })
  }, [quotePricing, employeeCount, register, errors, watchedValues])

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChangeInternal}
      aria-describedby={undefined}
    >
      <Modal.Body>
        <Modal.Title size='large'>Custom Thatch fees</Modal.Title>
        <div className='flex flex-col gap-4 pt-4'>
          {formFields}
          {!hasPricing && (
            <div className='text-sm text-gray-500'>
              No pricing found for this quote.
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Modal.Button
            variant='secondary'
          >
            Cancel
          </Modal.Button>
          <Modal.Button
            variant='primary'
            onClick={onClickSave}
          >Save
          </Modal.Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  )
}
